import React, { useMemo, useState } from "react";
import { ThemeProvider } from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import { token } from "../../../../theme";
import recycleBinIcon from "../../../../assets/images/recycle_bin_full-3.png";
import {
  AddressField,
  AddressRow,
  DetailsRow,
  DetailsTable,
  ExplorerBody,
  ExplorerContainer,
  MainPane,
  StatusBar,
  StatusPane,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  WebViewIcon,
  WebViewPanel,
  WebViewRule,
  WebViewText,
  WebViewTitle,
} from "../../../../components/Explorer/ExplorerStyles";
import {
  DeleteGlyph,
  FileIcon,
  FileKind,
  PropertiesGlyph,
  RestoreGlyph,
  SelectAllGlyph,
} from "../../../../components/Explorer/icons";
import { EmptyBinMessage } from "./RecycleBinStyles";
import { useDialog } from "../../utils/useDialog";

interface DeletedItem {
  id: string;
  name: string;
  kind: FileKind;
  bytes: number;
  dateDeleted: string;
  originalLocation: string;
  // flavour text shown in the side panel / properties dialog
  note: string;
  // some things are too cursed to restore
  restoreError?: string;
}

const INITIAL_ITEMS: DeletedItem[] = [
  {
    id: "portfolio-v1",
    name: "portfolio_v1.html",
    kind: "html",
    bytes: 12_288,
    dateDeleted: "2021-03-02",
    originalLocation: "C:\\Users\\Marcel\\Desktop",
    note: "My very first portfolio. 14 <marquee> tags, a visitor counter and a MIDI soundtrack. It had to go.",
  },
  {
    id: "resume",
    name: "Resume_FINAL_final_v3.pdf",
    kind: "pdf",
    bytes: 250_880,
    dateDeleted: "2025-01-15",
    originalLocation: "C:\\Users\\Marcel\\Documents",
    note: "Superseded by Resume_FINAL_final_v4.pdf. The circle of life.",
  },
  {
    id: "node-modules",
    name: "node_modules",
    kind: "folder",
    bytes: 1_288_490_188,
    dateDeleted: "2025-02-11",
    originalLocation: "C:\\Projects\\crispy-duck",
    note: "Heavier than the rest of the hard drive combined. Can be regenerated with npm install.",
    restoreError:
      "Cannot restore node_modules: there is not enough free space on the disk. Or in the universe.",
  },
  {
    id: "jquery",
    name: "jquery-spaghetti.js",
    kind: "code",
    bytes: 90_112,
    dateDeleted: "2022-08-19",
    originalLocation: "C:\\Projects\\legacy",
    note: "$(document).ready() nested seven levels deep. Rewritten in React and never spoken of again.",
  },
  {
    id: "ie6",
    name: "ie6-hacks.css",
    kind: "code",
    bytes: 4_096,
    dateDeleted: "2020-06-30",
    originalLocation: "C:\\Projects\\legacy",
    note: "* html .clearfix { zoom: 1; } and other ancient incantations.",
  },
  {
    id: "mockup",
    name: "Untitled-1.psd",
    kind: "image",
    bytes: 35_651_584,
    dateDeleted: "2024-11-04",
    originalLocation: "C:\\Users\\Marcel\\Pictures",
    note: "A mockup with 212 layers, all named 'Layer copy copy'.",
  },
  {
    id: "todo",
    name: "TODO.txt",
    kind: "text",
    bytes: 1_024,
    dateDeleted: "2025-09-01",
    originalLocation: "C:\\Users\\Marcel\\Desktop",
    note: "1. Finish portfolio  2. Add animations  3. Add more animations  4. ??? ",
  },
  {
    id: "backup",
    name: "backup_code_OLD.zip",
    kind: "zip",
    bytes: 6_081_740,
    dateDeleted: "2023-05-05",
    originalLocation: "C:\\Projects",
    note: "Before I discovered git. Contains folders named 'final', 'final2' and 'final-USE-THIS'.",
  },
];

type SortKey = "name" | "originalLocation" | "dateDeleted" | "bytes";

const COLUMNS: { key: SortKey; label: string; width: string }[] = [
  { key: "name", label: "Name", width: "34%" },
  { key: "originalLocation", label: "Original Location", width: "30%" },
  { key: "dateDeleted", label: "Date Deleted", width: "18%" },
  { key: "bytes", label: "Size", width: "18%" },
];

export const formatBytes = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)}GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

const formatDate = (date: string) => new Date(date).toLocaleDateString("en-GB");

const RecycleBin: React.FC = () => {
  const [items, setItems] = useState<DeletedItem[]>(INITIAL_ITEMS);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: "dateDeleted",
    asc: false,
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const showDialog = useDialog();

  const sortedItems = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    });
    return sort.asc ? sorted : sorted.reverse();
  }, [items, sort]);

  const selectedItems = items.filter((item) => selectedIds.includes(item.id));
  const focusedItem = selectedItems.length === 1 ? selectedItems[0] : undefined;
  const totalBytes = items.reduce((sum, item) => sum + item.bytes, 0);
  const selectedBytes = selectedItems.reduce(
    (sum, item) => sum + item.bytes,
    0
  );

  const handleRowClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setStatusMessage(null);
    if (e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    } else {
      setSelectedIds([id]);
    }
  };

  const removeItems = (ids: string[]) => {
    setItems((prev) => prev.filter((item) => !ids.includes(item.id)));
    setSelectedIds([]);
  };

  const handleRestore = () => {
    const blocked = selectedItems.find((item) => item.restoreError);
    if (blocked) {
      showDialog({
        windowTitle: "Error Restoring File",
        title: "Restore failed",
        content: blocked.restoreError,
      });
      return;
    }
    const restorable = selectedItems.map((item) => item.id);
    removeItems(restorable);
    setStatusMessage(
      restorable.length === 1
        ? `Restored '${selectedItems[0].name}' to ${selectedItems[0].originalLocation}`
        : `Restored ${restorable.length} items`
    );
  };

  const handleDelete = () => {
    const ids = selectedItems.map((item) => item.id);
    showDialog({
      windowTitle: "Confirm File Delete",
      content:
        ids.length === 1
          ? `Are you sure you want to delete '${selectedItems[0].name}'? It'll be gone forever (well, until you refresh).`
          : `Are you sure you want to delete these ${ids.length} items?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            removeItems(ids);
            setStatusMessage(`Deleted ${ids.length} item(s)`);
          },
        },
        { label: "No" },
      ],
    });
  };

  const handleEmpty = () => {
    showDialog({
      windowTitle: "Confirm Multiple File Delete",
      content: `Are you sure you want to delete all ${items.length} items in the Recycle Bin? That's ${formatBytes(totalBytes)} of questionable decisions.`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            removeItems(items.map((item) => item.id));
            setStatusMessage("Recycle Bin emptied. Fresh start!");
          },
        },
        { label: "No" },
      ],
    });
  };

  const handleProperties = (item: DeletedItem | undefined = focusedItem) => {
    if (!item) return;
    showDialog({
      windowTitle: `${item.name} Properties`,
      title: item.name,
      silent: true,
      content: (
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          <div>
            Type:{" "}
            {item.kind === "folder"
              ? "File Folder"
              : `${item.kind.toUpperCase()} File`}
          </div>
          <div>Origin: {item.originalLocation}</div>
          <div>
            Size: {formatBytes(item.bytes)} ({item.bytes.toLocaleString()}{" "}
            bytes)
          </div>
          <div>Deleted: {formatDate(item.dateDeleted)}</div>
          <p style={{ marginTop: 8 }}>{item.note}</p>
        </div>
      ),
    });
  };

  const toggleSort = (key: SortKey) =>
    setSort((prev) => ({ key, asc: prev.key === key ? !prev.asc : true }));

  return (
    <ThemeProvider theme={token}>
      <ExplorerContainer>
        <Toolbar>
          <ToolbarButton
            onClick={handleRestore}
            disabled={!selectedItems.length}
          >
            <RestoreGlyph />
            Restore
          </ToolbarButton>
          <ToolbarButton
            onClick={handleDelete}
            disabled={!selectedItems.length}
          >
            <DeleteGlyph />
            Delete
          </ToolbarButton>
          <ToolbarButton
            onClick={() => handleProperties()}
            disabled={!focusedItem}
          >
            <PropertiesGlyph />
            Properties
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton
            onClick={() => setSelectedIds(items.map((item) => item.id))}
            disabled={!items.length}
          >
            <SelectAllGlyph />
            Select All
          </ToolbarButton>
          <ToolbarButton onClick={handleEmpty} disabled={!items.length}>
            <img src={recycleBinIcon} alt="" />
            Empty Bin
          </ToolbarButton>
        </Toolbar>
        <AddressRow>
          Address
          <AddressField>
            <img src={recycleBinIcon} alt="" />
            Recycle Bin
          </AddressField>
        </AddressRow>

        <ExplorerBody>
          <WebViewPanel>
            <WebViewIcon>
              {focusedItem ? (
                <FileIcon kind={focusedItem.kind} />
              ) : (
                <img src={recycleBinIcon} alt="" />
              )}
            </WebViewIcon>
            <WebViewTitle>
              {focusedItem ? focusedItem.name : "Recycle Bin"}
            </WebViewTitle>
            <WebViewRule />
            {focusedItem ? (
              <>
                <WebViewText>{focusedItem.note}</WebViewText>
                <WebViewText>
                  <b>Original location:</b>
                  <br />
                  {focusedItem.originalLocation}
                </WebViewText>
                <WebViewText>
                  <b>Deleted:</b> {formatDate(focusedItem.dateDeleted)}
                  <br />
                  <b>Size:</b> {formatBytes(focusedItem.bytes)}
                </WebViewText>
              </>
            ) : selectedItems.length > 1 ? (
              <WebViewText>
                <b>{selectedItems.length} items selected.</b>
                <br />
                Total size: {formatBytes(selectedBytes)}
              </WebViewText>
            ) : (
              <>
                <WebViewText>
                  This folder contains files and folders that have been deleted
                  over the years. Every developer has a few skeletons in here.
                </WebViewText>
                <WebViewText>
                  Select an item to read about it. Use <b>Restore</b> to put it
                  back, or <b>Empty Bin</b> to start fresh.
                </WebViewText>
              </>
            )}
          </WebViewPanel>

          <MainPane onClick={() => setSelectedIds([])}>
            {items.length === 0 ? (
              <EmptyBinMessage>
                <img src={recycleBinIcon} alt="" />
                <p>The Recycle Bin is empty.</p>
              </EmptyBinMessage>
            ) : (
              <DetailsTable>
                <colgroup>
                  {COLUMNS.map((column) => (
                    <col key={column.key} style={{ width: column.width }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        onClick={() => toggleSort(column.key)}
                      >
                        {column.label}
                        {sort.key === column.key && (sort.asc ? " ▲" : " ▼")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence initial={false}>
                    {sortedItems.map((item) => (
                      <DetailsRow
                        as={motion.tr}
                        key={item.id}
                        layout
                        exit={{
                          opacity: 0,
                          x: 40,
                          transition: { duration: 0.2 },
                        }}
                        $selected={selectedIds.includes(item.id)}
                        onClick={(e: React.MouseEvent) =>
                          handleRowClick(e, item.id)
                        }
                        onDoubleClick={() => handleProperties(item)}
                      >
                        <td>
                          <FileIcon kind={item.kind} />
                          <span>{item.name}</span>
                        </td>
                        <td>{item.originalLocation}</td>
                        <td>{formatDate(item.dateDeleted)}</td>
                        <td>{formatBytes(item.bytes)}</td>
                      </DetailsRow>
                    ))}
                  </AnimatePresence>
                </tbody>
              </DetailsTable>
            )}
          </MainPane>
        </ExplorerBody>

        <StatusBar>
          <StatusPane $grow>
            {statusMessage ??
              (selectedItems.length
                ? `${selectedItems.length} object(s) selected`
                : `${items.length} object(s)`)}
          </StatusPane>
          <StatusPane>
            {formatBytes(selectedItems.length ? selectedBytes : totalBytes)}
          </StatusPane>
        </StatusBar>
      </ExplorerContainer>
    </ThemeProvider>
  );
};

export default RecycleBin;
